/* =====================================================================================
   Trade: quote to cash made physical. Quotes carry lines; orders carry delivery; stock
   is lot-numbered and expiry-dated; shipments take lots to customers and leave a trace
   a recall can follow. FEFO suggests the pick, a person confirms it, and the database
   refuses an oversell or an expired lot outright.
   ===================================================================================== */

import { BACKEND } from './db';
import { explain } from './dberrors';

const live = () => BACKEND === 'supabase';

export interface Product {
  id: string; sku: string; name: string; description?: string | null;
  category: string | null; unit: string; lifecycle: string; active: boolean;
  regulatory_status?: Record<string, string>;
}
export interface Quote {
  id: string; number: string; account_id: string; issue_date: string;
  valid_until: string | null; currency: string; subtotal: number; tax_total: number;
  total: number; status: string; notes: string | null;
}
export interface QuoteLine {
  id: string; quote_id: string; product_id: string; description: string | null;
  quantity: number; unit_price: number; discount_pct: number; tax_rate: number;
  net_amount: number; sort_order: number;
}
export interface Order {
  id: string; number: string; account_id: string; quote_id: string | null;
  customer_po_ref: string | null; order_date: string; currency: string;
  subtotal: number; tax_total: number; total: number; status: string;
  supply_basis: string; ship_to: Record<string, string>;
}
export interface OrderLine {
  id: string; order_id: string; product_id: string; description: string | null;
  quantity: number; unit_price: number; quantity_delivered: number; net_amount: number;
}
export interface StockRow {
  lot_id: string; product_id: string; sku: string; product_name: string;
  lot_number: string; expiry_date: string | null; location: string; status: string;
  on_hand: number;
}
export interface Shipment {
  id: string; number: string; order_id: string; status: string; courier: string | null;
  tracking_ref: string | null; tracking_url: string | null; condition: string;
  temp_logger_id: string | null; excursion: boolean; dispatched_on: string | null;
  delivered_on: string | null; ship_to: Record<string, string>;
}
export interface FefoSuggestion { lot_id: string; lot_number: string; expiry_date: string | null; take: number }
export interface TraceRow {
  lot_number: string; sku: string; product_name: string; expiry_date: string | null;
  shipment_number: string; dispatched_on: string | null; courier: string | null;
  tracking_ref: string | null; quantity: number; order_number: string;
  account_name: string; ship_to: Record<string, string>;
}

export const SHIP_CONDITIONS: [string, string][] = [
  ['ambient', 'Ambient'], ['cold_2_8', '2-8 °C'], ['minus_20', '-20 °C'],
  ['minus_80', '-80 °C'], ['dry_ice', 'Dry ice'], ['ln2', 'Liquid nitrogen'],
];
export const SUPPLY_BASES: [string, string][] = [
  ['ruo', 'Research use only'], ['ce_ivd', 'CE-IVD'], ['ukca', 'UKCA'],
  ['fda', 'FDA'], ['other', 'Other'],
];

async function sb() { const { supabase } = await import('./supabase'); return supabase(); }

export async function fetchProducts(): Promise<Product[]> {
  if (!live()) return [];
  const { data, error } = await (await sb()).schema('pipeline').from('products')
    .select('id, sku, name, description, category, unit, lifecycle, active, regulatory_status')
    .order('name');
  if (error) throw new Error(explain(error.message, 'read the product list'));
  return (data ?? []) as Product[];
}

export const PRODUCT_CATEGORIES = ['assay', 'kit', 'instrument', 'software', 'service', 'consumable'];
export const PRODUCT_LIFECYCLES: [string, string][] = [
  ['concept', 'Concept (not supplyable)'], ['development', 'Development (not supplyable)'],
  ['ruo', 'Research use only'], ['validation', 'Validation'],
  ['launched', 'Launched'], ['mature', 'Mature'], ['withdrawn', 'Withdrawn (not supplyable)'],
];

export async function createProduct(pr: { sku: string; name: string; description?: string;
  category: string; unit: string; lifecycle: string }): Promise<string> {
  const { data, error } = await (await sb()).schema('pipeline').from('products').insert({
    sku: pr.sku, name: pr.name, description: pr.description || null,
    category: pr.category, unit: pr.unit, lifecycle: pr.lifecycle, active: true,
  }).select('id').single();
  if (error) throw new Error(explain(error.message, 'create the product'));
  return data.id as string;
}

export async function updateProduct(id: string, patch: Partial<Product> & {
  regulatory_status?: Record<string, string> }) {
  const { error } = await (await sb()).schema('pipeline').from('products')
    .update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the product'));
}

export async function nextNumber(series: 'quote' | 'order' | 'shipment'): Promise<string> {
  const s = await sb();
  const { data: org } = await s.schema('app').rpc('current_org_id');
  const { data, error } = await s.schema('app').rpc('next_number', { p_org_id: org, p_series: series });
  if (error) throw new Error(explain(error.message, 'take the next document number'));
  return data as string;
}

/* ---------------------------------------------------------------- quotes */
export async function fetchQuotes(): Promise<Quote[]> {
  if (!live()) return [];
  const { data, error } = await (await sb()).schema('ledger').from('quotes')
    .select('*').order('issue_date', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the quotes'));
  return (data ?? []) as Quote[];
}
export async function fetchQuoteLines(quoteId: string): Promise<QuoteLine[]> {
  const { data, error } = await (await sb()).schema('ledger').from('quote_lines')
    .select('*').eq('quote_id', quoteId).order('sort_order');
  if (error) throw new Error(explain(error.message, 'read the quote lines'));
  return (data ?? []) as QuoteLine[];
}
export async function createQuote(q: { accountId: string; currency: string; validUntil?: string;
  notes?: string; lines: { productId: string; description?: string; quantity: number;
  unitPrice: number; discountPct?: number; taxRate?: number }[] }): Promise<string> {
  const s = await sb();
  const number = await nextNumber('quote');
  const { data, error } = await s.schema('ledger').from('quotes').insert({
    number, account_id: q.accountId, currency: q.currency,
    valid_until: q.validUntil || null, notes: q.notes || null,
  }).select('id').single();
  if (error) throw new Error(explain(error.message, 'create the quote'));
  const rows = q.lines.map((l, i) => ({
    quote_id: data.id, product_id: l.productId, description: l.description || null,
    quantity: l.quantity, unit_price: l.unitPrice, discount_pct: l.discountPct ?? 0,
    tax_rate: l.taxRate ?? 0, sort_order: i,
  }));
  if (rows.length) {
    const { error: e2 } = await s.schema('ledger').from('quote_lines').insert(rows);
    if (e2) throw new Error(explain(e2.message, 'save the quote lines'));
  }
  return data.id as string;
}
export async function setQuoteStatus(id: string, status: string) {
  const { error } = await (await sb()).schema('ledger').from('quotes')
    .update({ status }).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the quote'));
}

/* ---------------------------------------------------------------- orders */
export async function fetchOrders(): Promise<Order[]> {
  if (!live()) return [];
  const { data, error } = await (await sb()).schema('ledger').from('orders')
    .select('*').order('order_date', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the orders'));
  return (data ?? []) as Order[];
}
export async function fetchOrderLines(orderId: string): Promise<OrderLine[]> {
  const { data, error } = await (await sb()).schema('ledger').from('order_lines')
    .select('*').eq('order_id', orderId).order('sort_order');
  if (error) throw new Error(explain(error.message, 'read the order lines'));
  return (data ?? []) as OrderLine[];
}
export async function createOrder(o: { accountId: string; currency: string;
  supplyBasis: string; customerPoRef?: string; shipToCountry?: string; shipToAddress?: string;
  lines: { productId: string; description?: string; quantity: number; unitPrice: number;
    discountPct?: number; taxRate?: number }[] }): Promise<string> {
  const s = await sb();
  const number = await nextNumber('order');
  const shipTo: Record<string, string> = {};
  if (o.shipToCountry) shipTo.country = o.shipToCountry;
  if (o.shipToAddress) shipTo.address = o.shipToAddress;
  const { data, error } = await s.schema('ledger').from('orders').insert({
    number, account_id: o.accountId, currency: o.currency, supply_basis: o.supplyBasis,
    customer_po_ref: o.customerPoRef || null, ship_to: shipTo, status: 'draft',
  }).select('id').single();
  if (error) throw new Error(explain(error.message, 'create the order'));
  const rows = o.lines.map((l, i) => ({
    order_id: data.id, product_id: l.productId, description: l.description || null,
    quantity: l.quantity, unit_price: l.unitPrice, discount_pct: l.discountPct ?? 0,
    tax_rate: l.taxRate ?? 0, sort_order: i,
  }));
  if (rows.length) {
    const { error: e2 } = await s.schema('ledger').from('order_lines').insert(rows);
    if (e2) {
      await s.schema('ledger').from('orders').delete().eq('id', data.id);
      throw new Error(explain(e2.message, 'save the order lines'));
    }
  }
  return number;
}

export async function updateOrder(id: string, patch: Partial<Order>) {
  const { error } = await (await sb()).schema('ledger').from('orders')
    .update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the order'));
}

/* ---------------------------------------------------------------- stock */
export async function fetchStock(): Promise<StockRow[]> {
  if (!live()) return [];
  const { data, error } = await (await sb()).schema('ledger').from('stock_on_hand')
    .select('*').order('expiry_date', { ascending: true, nullsFirst: false });
  if (error) throw new Error(explain(error.message, 'read the stock'));
  return (data ?? []) as StockRow[];
}
export async function receiveLot(r: { productId: string; lotNumber: string;
  expiryDate?: string; quantity: number; location?: string; notes?: string }) {
  const { error } = await (await sb()).schema('ledger').from('stock_lots').insert({
    product_id: r.productId, lot_number: r.lotNumber, expiry_date: r.expiryDate || null,
    quantity_received: r.quantity, location: r.location || 'Main store', notes: r.notes || null,
  });
  if (error) throw new Error(explain(error.message, 'receive the lot'));
}
export async function adjustStock(lotId: string, movement: 'adjust_down' | 'adjust_up' | 'scrap',
  quantity: number, reason: string) {
  const { error } = await (await sb()).schema('ledger').from('stock_movements')
    .insert({ lot_id: lotId, movement, quantity, reason });
  if (error) throw new Error(explain(error.message, 'record the stock movement'));
}
export async function setLotStatus(lotId: string, status: string) {
  const { error } = await (await sb()).schema('ledger').from('stock_lots')
    .update({ status }).eq('id', lotId);
  if (error) throw new Error(explain(error.message, 'update the lot'));
}
export async function fefoSuggest(productId: string, quantity: number): Promise<FefoSuggestion[]> {
  const { data, error } = await (await sb()).schema('ledger')
    .rpc('fefo_suggest', { p_product: productId, p_quantity: quantity });
  if (error) throw new Error(explain(error.message, 'suggest the pick'));
  return (data ?? []) as FefoSuggestion[];
}

/* ---------------------------------------------------------------- shipments */
export async function fetchShipments(): Promise<Shipment[]> {
  if (!live()) return [];
  const { data, error } = await (await sb()).schema('ledger').from('shipments')
    .select('*').order('created_at', { ascending: false });
  if (error) throw new Error(explain(error.message, 'read the shipments'));
  return (data ?? []) as Shipment[];
}
export async function createShipment(s: { orderId: string; courier?: string;
  trackingRef?: string; trackingUrl?: string; condition: string; tempLoggerId?: string;
  shipTo?: Record<string, string>;
  lines: { orderLineId: string; lotId: string; quantity: number }[] }): Promise<string> {
  const c = await sb();
  const number = await nextNumber('shipment');
  const { data, error } = await c.schema('ledger').from('shipments').insert({
    number, order_id: s.orderId, courier: s.courier || null,
    tracking_ref: s.trackingRef || null, tracking_url: s.trackingUrl || null,
    condition: s.condition, temp_logger_id: s.tempLoggerId || null,
    ship_to: s.shipTo ?? {},
  }).select('id').single();
  if (error) throw new Error(explain(error.message, 'create the shipment'));
  const rows = s.lines.map((l) => ({
    shipment_id: data.id, order_line_id: l.orderLineId, lot_id: l.lotId, quantity: l.quantity,
  }));
  const { error: e2 } = await c.schema('ledger').from('shipment_lines').insert(rows);
  if (e2) {
    await c.schema('ledger').from('shipments').delete().eq('id', data.id);
    throw new Error(explain(e2.message, 'allocate the lots'));
  }
  return data.id as string;
}
export async function setShipmentStatus(id: string, patch: Partial<Shipment>) {
  const { error } = await (await sb()).schema('ledger').from('shipments')
    .update(patch).eq('id', id);
  if (error) throw new Error(explain(error.message, 'update the shipment'));
}
export async function fetchLotTrace(lotNumber?: string): Promise<TraceRow[]> {
  let q = (await sb()).schema('ledger').from('lot_trace').select('*');
  if (lotNumber) q = q.ilike('lot_number', `%${lotNumber}%`);
  const { data, error } = await q.order('dispatched_on', { ascending: false });
  if (error) throw new Error(explain(error.message, 'trace the lot'));
  return (data ?? []) as TraceRow[];
}
